import React, { useEffect, useState } from 'react'
import { InterfaceRoutes } from '../../interfaces/interfaceRoutes'
import './quienes-somos.css'
import { getQuienesSomos, getQuienesSomosHero } from '../../config/services/strapi/quienes-somos'
import TextSkeleton from '../../components/Skeleton/TextSkeleton'
import ImageSkeleton from '../../components/Skeleton/ImageSkeleton'

const renderChildren = (children: any[]) => {
  if (!children?.length) return null

  return children.map((c: any, ci: number) => {
    const txt: string = c?.text || ''
    const keyBase = `rc-${ci}-${(txt || '').slice(0, 12).split(/\s+/).join('-')}`
    if (c?.bold) {
      return <strong key={`${keyBase}-b`}>{txt}</strong>
    }

    if (txt.includes('\n')) {
      const parts = txt.split('\n')
      return parts.map((line, li) => (
        <React.Fragment key={`${keyBase}-${li}`}>
          {line}
          {li < parts.length - 1 ? <br /> : null}
        </React.Fragment>
      ))
    }

    return <React.Fragment key={`${keyBase}-t`}>{txt}</React.Fragment>
  })
}

const renderRichBlocks = (blocks: any, keyPrefix = '', firstAsHeading = false) => {
  if (!blocks) return null

  if (Array.isArray(blocks)) {
    return blocks.map((block: any, index: number) => {
      const rawChildren = block?.children || []
      const children = rawChildren.filter((c: any) => (c?.text || '').trim() !== 'Drag')
      const hasText = children.some((c: any) => c?.text && c.text.trim().length > 0)
      if (!hasText) return null

      if (firstAsHeading && index === 0) {
        return (
          <h2 key={`${keyPrefix}-h2-${index}`} className="qs-hero-title">
            {children.map((c: any, i: number) => (
              <React.Fragment key={`${keyPrefix}-h-${i}-${(c?.text||'').slice(0,10).split(/\s+/).join('-')}`}>{c?.text || ''}</React.Fragment>
            ))}
          </h2>
        )
      }

      const hasBold = children.some((c: any) => c?.bold)
      if (hasBold) {
        const nodes: any[] = []
        let accum: any[] = []

        const pushAccum = () => {
          if (accum.length) {
            const key = `${keyPrefix}-p-${index}-${nodes.length}`
            nodes.push(<p key={key}>{renderChildren(accum)}</p>)
            accum = []
          }
        }

        children.forEach((c: any, ci: number) => {
          if (c?.bold) {
            pushAccum()
            const hk = `${keyPrefix}-h3-${index}-${ci}`
            nodes.push(<h3 key={hk} className="qs-linea-titulo">{(c.text || '').trim()}</h3>)
          } else if ((c?.text || '').trim()) {
            accum.push(c)
          }
        })

        pushAccum()
        return <React.Fragment key={`${keyPrefix}-frag-${index}`}>{nodes}</React.Fragment>
      }

      return (
        <p key={`${keyPrefix}-p-${index}`}>{renderChildren(children)}</p>
      )
    })
  }

  if (typeof blocks === 'string') {
    return <p>{blocks}</p>
  }

  return null
}

const renderDirectorioFromBlocks = (blocks: any) => {
  if (!blocks) return null

  const items: Array<{ label: string; value: string }> = []
  const paragraphs: string[] = []

  const nodes: any[] = []

  blocks.forEach((block: any, bi: number) => {
    const children = block?.children || []
    const hasBold = children.some((c: any) => c?.bold)

    if (!hasBold && children.length > 0) {
      const text = children.map((c: any) => c?.text || '').join('')
      if (text.trim()) {
        nodes.push(<p key={`dir-block-${bi}-${(text||'').slice(0,12).split(/\s+/).join('-')}`}>{text}</p>)
      }
      return
    }

    const itemsForBlock: Array<{ label: string; value: string }> = []
    let currentLabel = ''
    children.forEach((c: any) => {
      if (c?.bold) {
        currentLabel = (c.text || '').trim()
        return
      }

      if (c?.text) {
        const lines = c.text.split('\n').map((s: string) => s.trim()).filter(Boolean)
        lines.forEach((ln: string) => {
          if (currentLabel) {
            itemsForBlock.push({ label: (currentLabel || '').replace(/\s+$/, ''), value: ln })
            currentLabel = ''
          } else {
            itemsForBlock.push({ label: '', value: ln })
          }
        })
      }
    })

    if (itemsForBlock.length) {
      const ulKey = `dir-list-${bi}-${itemsForBlock.map(it => ((it.label||'').slice(0,6)+'-'+(it.value||'').slice(0,6))).join('-')}`
      nodes.push(
        <ul className="qs-specs" key={ulKey}>
          {itemsForBlock.map((it) => {
            const k = `dir-${(it.label || '').slice(0,12).split(/\s+/).join('-')}-${(it.value || '').slice(0,12).split(/\s+/).join('-')}`
            return (
              <li key={k}>
                <span className="qs-key">{it.label}</span>
                <span className="qs-value">{it.value}</span>
              </li>
            )
          })}
        </ul>
      )
    }
  })

  return <>{nodes}</>
}

const renderDetalleFromBlocks = (blocks: any) => {
  if (!blocks) return null

  const nodes: any[] = []

  const firstBlock = blocks[0]
  let i = 0
  const firstChildren = (firstBlock?.children || []).filter((c: any) => (c?.text || '').trim() !== 'Drag')
  const firstHasBold = firstChildren.some((c: any) => c?.bold)
    if (!firstHasBold && firstChildren.length > 0) {
      const text = firstChildren.map((c: any) => c?.text || '').join('')
      if (text.trim()) {
        nodes.push(<p key={`detalle-intro-${(text||'').slice(0,12).split(/\s+/).join('-')}`}>{text}</p>)
      }
      i = 1
    }

    while (i < blocks.length) {
      const block = blocks[i]
      const children = (block?.children || []).filter((c: any) => (c?.text || '').trim() !== 'Drag')
      if (!children.length) {
        i++
        continue
      }

      const boldIndex = children.findIndex((c: any) => c?.bold)
      if (boldIndex !== -1) {
        for (let bi = 0; bi < children.length; bi++) {
          const c = children[bi]
          if (c?.bold) {
            const title = (c.text || '').trim()
            const contentParts: string[] = []

            for (let j = bi + 1; j < children.length; j++) {
              if (children[j]?.text) contentParts.push(children[j].text)
            }

            let k = i + 1
            while (k < blocks.length) {
              const nextChildren = (blocks[k]?.children || []).filter((nc: any) => (nc?.text || '').trim() !== 'Drag')
              const nextHasBold = nextChildren.some((nc: any) => nc?.bold)
              if (nextHasBold) break
              const txt = nextChildren.map((nc: any) => nc?.text || '').join('\n')
              if (txt.trim()) contentParts.push(txt)
              k++
            }

            nodes.push(
              <div className="qs-linea" key={`qs-linea-${title.slice(0,12).split(/\s+/).join('-')}`}>
                <h3 className="qs-linea-titulo">{title}</h3>
                {contentParts.length ? <p>{contentParts.join('\n')}</p> : null}
              </div>
            )

            i = Math.max(i, k - 1)
          }
        }
        i++
        continue
      }

      const text = children.map((c: any) => c?.text || '').join('')
      if (text.trim()) {
        nodes.push(<p key={`detalle-par-${i}-${(text||'').slice(0,12).split(/\s+/).join('-')}`}>{text}</p>)
      }
      i++
    }

    return <>{nodes}</>
}

const getMediaUrl = (media: any) => {
  const base = process.env.REACT_APP_STRAPI_ENDPOINT || 'http://localhost:1337'
  const raw = media?.url || media?.data?.attributes?.url || ''
  if (!raw) return ''
  if (raw.startsWith('http')) return raw
  return `${base}${raw}`
}

const QuienesSomos = (_props: InterfaceRoutes) => {
  const [loading, setLoading] = useState(true)
  
  const [heroImage, setHeroImage] = useState<string>('')
  const [qsData, setQsData] = useState<any>(null)

  

  useEffect(() => {
    const load = async () => {
      try {
        const [heroRes, qsRes] = await Promise.all([getQuienesSomosHero(), getQuienesSomos()])

        const heroAttrs = heroRes?.attributes || heroRes || {}

        Object.keys(heroAttrs).forEach((k) => {
          const v = heroAttrs[k]
          if (v?.url) {
            setHeroImage(getMediaUrl(v))
          }
        })

        const qsAttrs = qsRes?.attributes || qsRes || null
        setQsData(qsAttrs)
      } catch (err) {
        console.error('Error cargando Quienes Somos:', err)
      } finally {
        setLoading(false)
      }
    }

    load()
  }, [])

  return (
    <div className="quienes-page">
      {loading && (
        <div id="hero-qs-wrapper">
          <ImageSkeleton className="home-hero-skeleton" />
        </div>
      )}

      {!loading && (
        <div id="hero-qs-wrapper">
          <div id="hero-qs-image">
            {heroImage ? <img src={heroImage} alt="Quiénes Somos - Fundación Gasco" /> : null}
          </div>
        </div>
      )}

      <div id="main-content">
        <div className="page-content qs-content">
          <div className="qs-header">
            {loading ? <TextSkeleton /> : <h1 className="qs-title">Quiénes Somos</h1>}

            <div className="qs-subtitle">
              {loading ? <TextSkeleton /> : renderRichBlocks(qsData?.descripcion, 'qs-desc')}
            </div>
          </div>

          <div className="qs-columns">
            <div className="qs-col">
              <h2 className="qs-col-title">Directorio</h2>
              {loading ? (
                <TextSkeleton />
              ) : (
                <>
                  {renderDirectorioFromBlocks(qsData?.directorio)}
                  <p className="qs-legal">{qsData?.legal || ''}</p>
                </>
              )}
            </div>

            <div className="qs-col">
              <h2 className="qs-col-title">Lineas de Trabajo</h2>
              {loading ? <TextSkeleton /> : renderDetalleFromBlocks(qsData?.detalle)}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default QuienesSomos
